import { ServiceBase } from "../ServiceBase";
import { IAbTestingFeatureProperties } from "../sdk/cgp-sdk.interface";
import {
    IAbTestingAiSortData,
    IAbTestingFeatureDataProviderService
} from "./ab-testing-feature-data-provider.service.interface";
import { abTestingFeatureIsValid } from "../../../utils/ab-testing-feature-check";
import {
    FEATURE_NAME_SAFE_ZONE_TOGGLE,
    FEATURE_NAME_GRAPHYTE_LISTS,
    FEATURE_NAME_TOP_BANNER_REDESIGN,
    FEATURE_NAME_SEARCH_GAME_SUGGESTED_BY_AI,
    FEATURE_NAME_PLAYERS_LIKE_YOU_GAMES_LIST_TYPE_ID,
    FEATURE_NAME_LIST_ON_TOP,
    FEATURE_NAME_VIDEO_LIVE_STREAM,
    FEATURE_NAME_AI_SORT_GAME_LIST,
    FEATURE_NAME_EXIT_POPUP_SQUARE_ICON,
    FEATURE_NAME_BECAUSE_YOU_PLAYED_LIST
} from "../../../common/const/ab-testing-names";
import { action, observable } from 'mobx';
import { IServiceFactoryExtended } from '../service-factory-extended.interface';
import { Check } from "@sparkware/app-utils";


export class AbTestingFeatureDataProviderService extends ServiceBase implements IAbTestingFeatureDataProviderService {

    constructor(services: IServiceFactoryExtended) {
        super(services);

        this.reaction(() => this.services.user.isAuthorized,
            () => {
                this._playersLikeYouGamesListTypeId();
                this._setIsVideoLiveStreamEnabled();
                this._setIsExitPopupSquareIconsEnabled();
                this._setIsBecauseYouPlayedEnabled();
            }, {
            fireImmediately: true
        });
    }

    private get _features(): { [featureName: string]: IAbTestingFeatureProperties } {
        return this.services.sdk.getAbTestingData() || {};
    }

    private _hasFeature(featureName: string) {
        const feature = this._features[featureName];
        if (!feature || !feature.Properties) {
            return false;
        }

        return true;
    }

    get aiSortGamesLists(): IAbTestingAiSortData {

        const aiSortGamesListsFeatureData = this._features[FEATURE_NAME_AI_SORT_GAME_LIST];

        const result: IAbTestingAiSortData = { arenas: [] };
        if (abTestingFeatureIsValid(aiSortGamesListsFeatureData, ["arenas"]) && Check.isString(aiSortGamesListsFeatureData.Properties.arenas)) {
            try {
                result.arenas = JSON.parse(aiSortGamesListsFeatureData.Properties.arenas.replace(/'/g, '"'));
            } catch (e) {
                this.services.logger.error(`Failed to parse AB Test data for feature "${FEATURE_NAME_AI_SORT_GAME_LIST}".\n${e}`);
            }
        }
        return result;
    }

    get searchGameSuggestionByAI(): string | null {
        if (this._hasFeature(FEATURE_NAME_SEARCH_GAME_SUGGESTED_BY_AI)) {
            const abTestingSearchGameSuggestionByAI = this._features[FEATURE_NAME_SEARCH_GAME_SUGGESTED_BY_AI];
            if (abTestingFeatureIsValid(abTestingSearchGameSuggestionByAI, ["aiType"])) {
                return abTestingSearchGameSuggestionByAI.Properties?.aiType?.toLowerCase();
            }
        }

        return null;
    }

    @observable.ref playersLikeYouGamesListTypeId = null;


    @action.bound
    private _playersLikeYouGamesListTypeId(): void {
        if (this._hasFeature(FEATURE_NAME_PLAYERS_LIKE_YOU_GAMES_LIST_TYPE_ID)) {
            const abTestingPlayersLikeYouGamesListTypeIdAIData = this._features[FEATURE_NAME_PLAYERS_LIKE_YOU_GAMES_LIST_TYPE_ID];
            if (abTestingFeatureIsValid(abTestingPlayersLikeYouGamesListTypeIdAIData, ["listID"])) {
                this.playersLikeYouGamesListTypeId = abTestingPlayersLikeYouGamesListTypeIdAIData.Properties?.listID?.toLowerCase();
            } else {
                this.playersLikeYouGamesListTypeId = null;
            }
        } else {
            this.playersLikeYouGamesListTypeId = null;
        }
    }

    get listOnTop(): number | null {
        if (this._hasFeature(FEATURE_NAME_LIST_ON_TOP)) {
            const abTestingListOnTopData = this._features[FEATURE_NAME_LIST_ON_TOP];
            if (abTestingFeatureIsValid(abTestingListOnTopData, ["listID"])) {
                return parseInt(abTestingListOnTopData.Properties?.listID);
            }
        }
        return null;
    }

  get enableSafeZone(): boolean {

      const currentRegulationCode = this.services.regulation.getRegulationCode();
      const enabledForRegulation = this.services.configuration.servicesConfigs.SafezoneLoader.enabledForRegulation;
      if (enabledForRegulation && enabledForRegulation[currentRegulationCode]) {
          return enabledForRegulation[currentRegulationCode];
      }

      if (this._hasFeature(FEATURE_NAME_SAFE_ZONE_TOGGLE)) {
          const abTestingSafeZoneToggleData = this._features[FEATURE_NAME_SAFE_ZONE_TOGGLE];
          if (abTestingFeatureIsValid(abTestingSafeZoneToggleData, ["enableSafeZone"])) {
              return abTestingSafeZoneToggleData.Properties?.enableSafeZone.toLowerCase() === "true";
          }
          return false;
      } else {
          return Boolean(this.services.configuration.data.safezone?.enabled);
      }
  }

    get miniTopBannerComponentID(): number {
        //return 517046;

        if (this._hasFeature(FEATURE_NAME_TOP_BANNER_REDESIGN)) {
            const miniBannersFeature = this._features[FEATURE_NAME_TOP_BANNER_REDESIGN];
            if (abTestingFeatureIsValid(miniBannersFeature, ["featureToggle", "componentID"])) {
                if (miniBannersFeature.Properties.featureToggle.toLowerCase() === "true") {
                    return Number(miniBannersFeature.Properties.componentID?.toString() ?? "");
                }
            }
        }
        return 0;
    }

    get areGraphyteListsEnabled(): boolean {
        if (this._hasFeature(FEATURE_NAME_GRAPHYTE_LISTS)) {
            const featureData = this._features[FEATURE_NAME_GRAPHYTE_LISTS];
            if (abTestingFeatureIsValid(featureData, ["featureToggle"])) {
                return featureData.Properties.featureToggle.toLowerCase() === "true";
            }
        }
        return false;
    }

    @observable.ref isVideoLiveStreamEnabled: boolean = false;

    @action.bound
    _setIsVideoLiveStreamEnabled(): void {
        let isEnabled = false;
        if (this._hasFeature(FEATURE_NAME_VIDEO_LIVE_STREAM)) {
            const featureData = this._features[FEATURE_NAME_VIDEO_LIVE_STREAM];
            if (abTestingFeatureIsValid(featureData, ["featureToggle"])) {
                isEnabled = featureData.Properties.featureToggle.toLowerCase() === "true";
            }
        }
        this.isVideoLiveStreamEnabled = isEnabled;
    }


    @observable.ref isExitPopupSquareIconsEnabled: boolean = false;

    @action.bound
    _setIsExitPopupSquareIconsEnabled(): void {
        let isEnabled = false;
        if (this._hasFeature(FEATURE_NAME_EXIT_POPUP_SQUARE_ICON)) {
            const featureData = this._features[FEATURE_NAME_EXIT_POPUP_SQUARE_ICON];
            if (abTestingFeatureIsValid(featureData, ["featureToggle"])) {
                isEnabled = featureData.Properties.featureToggle.toLowerCase() === "true";
            }
        }
        this.isExitPopupSquareIconsEnabled = isEnabled;
    }

    @observable.ref isBecauseYouPlayedEnabled: boolean = false;

    @action.bound
    _setIsBecauseYouPlayedEnabled(): void {
        let isEnabled = false;
        if (this._hasFeature(FEATURE_NAME_BECAUSE_YOU_PLAYED_LIST)) {
            const featureData = this._features[FEATURE_NAME_BECAUSE_YOU_PLAYED_LIST];
            if (abTestingFeatureIsValid(featureData, ["featureToggle"])) {
                isEnabled = featureData.Properties.featureToggle.toLowerCase() === "true";
            }
        }
        this.isBecauseYouPlayedEnabled = isEnabled;
    }
}
